(ns nosco.util
  "Various utilities. We're trying to wrap date-related code here,
  so that we can easily swap it out with some other library, and also to
  ensure we load only individual functions from JS utilities, to avoid pulling
  in a bunch of unused code. Unfortunately Google Clojure / shadow-cljs can't/won't
  run node_modules dependencies through advanced optimizations..."
  (:require
   ["date-fns/add$default" :as date-add]
   ["date-fns/eachDayOfInterval$default" :as eachDayOfInterval]
   ["date-fns/endOfDay$default" :as -date-end-of-day]
   ["date-fns/isAfter$default" :as -date-after?]
   ["date-fns/isBefore$default" :as -date-before?]
   ["date-fns/lastDayOfMonth$default" :as date-last-day-of-month]
   ["date-fns/lastDayOfYear$default" :as date-last-day-of-year]
   ["date-fns/parseISO$default" :as parseIso*]
   ["date-fns/set$default" :as date-set]
   ["date-fns/setDate$default" :as date-set-date]
   ["date-fns/setDayOfYear$default" :as date-set-day-of-year]
   ["date-fns/startOfDay$default" :as -date-start-of-day]
   ["date-fns/sub$default" :as date-subtract]
   ["date-fns/toDate$default" :as toDate]
   ["react" :as React]
   ["react-intersection-observer" :as react-intersection-observer]
   [cljs-bean.core :as bean]
   [clojure.string :as string]
   [goog.object :as gobj]
   [hx.hooks :as hooks]
   [nosco.polyfills]
   [nosco.shared.email-checker :as email-checker]
   [nosco.util.format :as nosco-format]
   [reseda.react :as rr]))

(def date-after? -date-after?)
(def date-before? -date-before?)

(goog-define WORMHOLE-EXIT false)

(def ^{:const true} keycode-enter 13)

(defn redirect [path]
  (if WORMHOLE-EXIT
    (set! (.-location js/document) path)
    (js/console.log "Should redirect to " path)))

(defn- debounce-impl [query-timeout interval f]
  (fn [& args]
    (when @query-timeout
      (.clearTimeout js/window @query-timeout))
    (let [resolve-reject-container #js {}
          p (js/Promise. (fn [resolve reject]
                           (gobj/set resolve-reject-container "resolve" resolve)
                           (gobj/set resolve-reject-container "reject" reject)))
          resolve (gobj/get resolve-reject-container "resolve")
          reject (gobj/get resolve-reject-container "reject")]
      (reset! query-timeout
              (.setTimeout js/window (fn []
                                       (let [f-result (apply f args)]
                                         (if (instance? js/Promise f-result)
                                           (-> f-result
                                               (.then (fn [v] (resolve v)))
                                               (.catch (fn [v] (reject v))))
                                           (resolve f-result))))
                           interval))
      p)))

(defn use-debounce
  "React-friendly debounce: return a function that will only be called once per interval.
  The rules of hooks apply, so you can only call this inside a React component. For general
  debouncing outside of a React component try nosco.util/debounce.

  Returns a promise that resolves when the function f returns, ie when both the
  debounced delay has passed and the function has run."
  ;; goog.functions/debounce doesn't work in react because the debounced function
  ;; gets re-created at every render, so the timeout handle changes. This approach
  ;; uses a persistent timeout handle so that the timeout gets cleared properly.
  [interval f]
  (let [query-timeout (hooks/useIRef nil)]
    (debounce-impl query-timeout interval f)))

(defn debounce
  "! NOT React-friendly !

  Like goog.functions/debounce but the debounced function returns a promise that
  resolves when the function f returns, ie when both the debounced delay has passed
  and the function has run."
  [interval f]
  (let [query-timeout (atom nil)]
    (debounce-impl query-timeout interval f)))

(defn use-debounced-event-handler
  "The same as `use-debounce` but assumes one argument of type SyntheticEvent.
  The event is persisted before calling the debounced fn."
  [interval f]
  (let [debounced-f (use-debounce interval f)]
    (fn [^js event]
      ;; If the event is a React SyntheticEvent, make sure it's persisted so that it
      ;; doesn't get recycled before `f` is called
      (when (.-persist event)
        (.persist event))
      (debounced-f event))))

(def date-start-of-day -date-start-of-day)
(def date-end-of-day -date-end-of-day)

(defn epoch->local-date
  "Convert unix epoch to user's local date and set to start of day."
  [t]
  (-> t
      toDate
      date-start-of-day))

(defn zeroed-map-of-days-for-interval
  "Returns a map consisting of JS date keys and values of 0.
   There will be an entry for each day in between the two passed JS dates (both included)."
  [start-date end-date]
  (-> (eachDayOfInterval #js {:start start-date :end end-date})
      (zipmap (repeat 0))))

(defn ->date [date-like]
  (when date-like
    (if (inst? date-like)
      date-like
      (parseIso* date-like))))

(def ->inst ->date)

(defn truncate-date-for-grouping [d]
  (date-set (->date d) #js {:hours 12 :minutes 0 :seconds 0 :milliseconds 0}))

(defn set-date-components
  "Just a wraper around date-fns/date-set"
  [d components]
  (date-set (->date d) (clj->js components)))

(defn date-format
  ([d] (nosco-format/date-format (->date d)))
  ([d fmt-type] (nosco-format/date-format (->date d) fmt-type)))

(defn number-format
  ([x]
   (nosco-format/number-format x))
  ([x style]
   (nosco-format/number-format x style)))

(defn usePrevious
  ([value] (usePrevious value (React/useRef)))
  ([value ref]
   (let [v (hooks/useValue value)]
     (hooks/useEffect (fn []
                        (set! (.-current ref) v))
                      [v])
     (.-current ref))))

(defn read-file-as-data-url
  "Returns a Promise that resolves to a data URL for the file."
  [file]
  (js/Promise.
   (fn [resolve _reject]
     (let [reader (js/FileReader.)]
       (.addEventListener reader "load" #(resolve (.-result reader)))
       (.readAsDataURL reader file)))))

(defn read-file-as-text
  "Returns a Promise that resolves to the text of the file"
  [file]
  (js/Promise.
   (fn [resolve _reject]
     (let [reader (js/FileReader.)]
       (.addEventListener reader "load" #(resolve (.-result reader)))
       (.readAsText reader file)))))

(defn download-url
  "Download a URL. Creating and clicking an `a` element with a `download` attribute set.
  You could also do `window.location = url` but that broke the dev setup with shadow-cljs.
  and also messed up the history a little bit. This seems to work on all browsers incl. IE11."
  [url & [filename]]
  {:pre [(string? url)]}
  (let [a (.createElement js/document "a")
        _ (set! (.-style a) "display: none;")
        _ (set! (.-href a) url)
        _ (set! (.-download a) (or filename ""))
        _ (-> js/document .-body (.appendChild a))]
    (.click a)
    (-> js/document .-body (.removeChild a))))

;; Shortening of HTML text

(defn- get-child-nodes [n]
  (into [] (array-seq (.-childNodes n))))

(defn- remove-node [n]
  (when-let [p (.-parentNode n)]
    (.removeChild p n)))

(def ^:const element-node-type 1)
(def ^:const document-node-type 9)
(def ^:const document-fragment-node-type 11)
(def ^:const text-node-type 3)

(def ^{:dynamic true :private true} *remaining-text-length* nil)

(defn- shorten-html-text-impl [node]
  (if (= *remaining-text-length* 0)
    (remove-node node)
    (let [node-type (.-nodeType node)]
      (cond (or (= node-type element-node-type)
                (= node-type document-node-type)
                (= node-type document-fragment-node-type))
            (doseq [child (get-child-nodes node)]
              (shorten-html-text-impl child))
            (= node-type text-node-type)
            (let [text (.-nodeValue node)
                  text-length (count text)]
              (if (< text-length *remaining-text-length*)
                (set! *remaining-text-length* (- *remaining-text-length* text-length))
                (do
                  (gobj/set node "nodeValue"
                            (str (subs text 0 *remaining-text-length*)
                                 "..."))
                  (set! *remaining-text-length* 0))))
            :else (remove-node node)))))

(defn shorten-html-text
  "Shorten a DOM tree by walking the DOM nodes, counting the number of characters and stripping the
  remaining text/DOM nodes once the max-text-length has been reached.
  Returns the shortened DOM tree."
  [dom-node max-text-length]
  (binding [*remaining-text-length* max-text-length]
    (shorten-html-text-impl dom-node))
  dom-node)

(comment

  (def sample-description "Progressively grow enabled products whereas frictionless information. Appropriately syndicate high-payoff e-services with <u>extensive relationships</u>. Globally monetize premium niches rather than state of the art <strong>communities</strong>. Dynamically repurpose technically sound meta-services without economically sound initiatives. Quickly visualize client-based meta-services and multidisciplinary leadership.<br><br>
<ul>
<li>Proactively restore value-added core competencies.</li>
<li>Assertively communicate backend strategic theme areas with enterprise scenarios.<br></li>
</ul>
Progressively grow enabled products whereas frictionless information. Appropriately syndicate high-payoff e-services with <u>extensive relationships</u>. Globally monetize premium niches rather than state of the art <strong>communities</strong>. Dynamically repurpose technically sound meta-services without economically sound initiatives. Quickly visualize client-based meta-services and multidisciplinary leadership.<br><br>")

  (let [container (js/document.createElement "div")]
    (gobj/set container "innerHTML" sample-description)
    (.log js/console (shorten-html-text container 180))))

(defn delay-promise
  "Returns a Promise which adds a delay to the Promise p.
  Useful for testing Suspense boundaries."
  [p delay-ms]
  (let [resolve-reject-container #js {}
        p' (js/Promise. (fn [resolve reject]
                          (gobj/set resolve-reject-container "resolve" resolve)
                          (gobj/set resolve-reject-container "reject" reject)))
        resolve (.-resolve resolve-reject-container)
        reject (.-reject resolve-reject-container)
        p (.catch p (fn [err]
                      (reject err)))]
    (js/setTimeout
     #(.then p (fn [result]
                 (resolve result)))
     delay-ms)
    p'))

(defn non-blank
  "Returns the string s, or nil when s is blank."
  [s]
  (when-not (string/blank? s)
    s))

(defn positions
  [pred coll]
  (keep-indexed (fn [idx x]
                  (when (pred x)
                    idx))
                coll))

(defn useEventListener [ref event handler]
  (hooks/useEffect
   (fn []
     (let [el (.-current ref)]
       (.addEventListener el event handler)
       (fn [] (.removeEventListener el event handler))))))

(defn clamp [i lower upper] (min (max i lower) upper))

(defn is-nan? [v]
  (and (= js/Number (type v)) (js/isNaN v)))

(defn browser-handles-click? [event]
  (or (.-shiftKey event)
      (.-metaKey event)
      (> (.-button event) 0)))

(defn handles-left-right-arrows?
  "Does this node handle left-right arrow keys natively?"
  [dom-node]
  (let [tagName (string/lower-case (str (.-tagName dom-node)))
        editable (.getAttribute dom-node "contenteditable")]
    (or editable
        (contains? #{"input" "textarea"} tagName))))

(defn index-of
  "Returns the index of x in coll."
  [coll x]
  (first (keep-indexed (fn [i x'] (when (= x x') i)) coll)))

(defn genid
  "Returns a unique string."
  [prefix]
  (str (gensym prefix)))

(defn get-local-offset []
  (.getTimezoneOffset (js/Date.)))

(defn offset->seconds [offset]
  (- (* offset 60)))

(defn- offset->hours [offset]
  (- (/ offset 60)))

(defn offset->UTC-string [offset]
  (let [offset (offset->hours offset)]
    (str "UTC" (cond (zero? offset) nil
                     (neg? offset) (str "-" (- offset))
                     (pos? offset) (str "+" offset)
                     :else nil))))
(defn rem->px []
  (js/parseInt (.-fontSize (js/getComputedStyle js/document.documentElement))))

(if js/Intl.Segmenter
  ;; implemented in Chrome/Safari
  (let [segmenter (js/Intl.Segmenter.)]
    (defn char-count [text]
      (count (js/Array.from (.segment segmenter text))))
    (defn truncate-with-ellipsis [s n]
      (let [graphemes (js/Array.from (.segment segmenter s) (fn [x] (.-segment ^js x)))
            c (count graphemes)]
        (if (<= c n)
          s
          (str (string/join "" (take n graphemes))
               "…")))))
  ;; not in firefox :(
  (do
    (defn char-count
      "Mildly better char count, taking into account most common emojis."
      ;; https://blog.jonnew.com/posts/poo-dot-length-equals-two
      ;; hoping that Firefox implements the segmenter soon.
      [text]
      (count (js/Array.from text)))
    (defn truncate-with-ellipsis [s n]
      (let [chars (js/Array.from s)
            c (count chars)]
        (if (<= c n)
          s
          (str (string/join "" (take n chars))
               "…"))))))

(defn use-media-query [query]
  (let [media-list (.matchMedia js/window query)
        [matches set-matches] (hooks/useState (.-matches media-list))
        update-match (fn [] (set-matches (.-matches media-list)))]
    (hooks/useEffect (fn []
                       (if (nosco.polyfills/IE11?)
                         (.addListener media-list "change" update-match)
                         (.addEventListener media-list "change" update-match))
                       (fn []
                         (if (nosco.polyfills/IE11?)
                           (.removeListener media-list "change" update-match)
                           (.removeEventListener media-list "change" update-match))))
                     [query])
    matches))

(def MOBILE-QUERY "(max-width: 833px)")
(def TABLET-QUERY "(max-width: 1091px)")
;; SYNC with _variables.scss

(defn use-is-mobile? []
  (use-media-query
   MOBILE-QUERY))

(defn use-is-tablet? []
  (use-media-query
   TABLET-QUERY))

(defn move-nth
  "Given a vector v, indices from and to, moves the
   element at from to element at to such that all other
   elements are pushed around."
  [v from to]
  (if (empty? v) v
      (let [item (nth v from)
            v' (into (subvec v 0 from) (subvec v (inc from)))]
        (into (conj
               (subvec v' 0 to)
               item)
              (subvec v' to)))))

(defn vec-remove
  [coll pos]
  (into (subvec coll 0 pos) (subvec coll (inc pos))))

(defn use-scroll-to-top
  "Scroll the node at scroll-ref whenever something
   in the vector of condition changes."
  [scroll-ref conditions]
  (hooks/useLayoutEffect
   (fn []
     (when-let [node (.-current scroll-ref)]
       (set! (.-scrollTop node) 0))
     js/undefined)
   conditions))

(defn use-window-scroll-top
  [conditions]
  (hooks/useLayoutEffect
   (fn []
     (.scroll js/window 0 0)
     js/undefined)
   conditions))

(defn use-mounted?
  "Returns a function which returns true when the component is mounted."
  []
  (let [mounted-ref (React/useRef false)]
    (hooks/useEffect
     (fn []
       (set! (.-current mounted-ref) true)
       (fn []
         (set! (.-current mounted-ref) false)))
     [])
    (hooks/useCallback (fn [] (.-current mounted-ref)) [])))

(defn use-cancel-promise-on-unmount
  "Returns a fn which takes a Promise and returns a new Promise which will
  only resolve or reject when the component is mounted."
  []
  (let [mounted? (use-mounted?)]
    (hooks/useCallback
     (fn [promise]
       (js/Promise.
        (fn [resolve reject]
          (.then promise
                 #(when (mounted?) (resolve %))
                 #(when (mounted?) (reject %))))))
     [])))

(defn use-first-render? []
  (let [first-render-ref (React/useRef true)]
    (hooks/useEffect
     (fn []
       (gobj/set first-render-ref "current" false))
     [])
    (.-current first-render-ref)))

(defn use-scale-in-out [ref condition]
  (let [first-render? (use-first-render?)]
    (hooks/useEffect
     (fn []
       ;; Trigger a scale-in/scale-out animation, only when "condition" switches to true,
       ;; not during the first render
       (when (and (not first-render?) condition)
         (when-let [el (.-current ref)]
           (.. el -classList (add "nosco-scale-in-out"))
           (js/setTimeout
            (fn [] (.. el -classList (remove "nosco-scale-in-out")))
            500))))
     [condition])))

(defn use-in-view
  "Hook wrapping an IntersectionObserver.

  Returns a map with keys:
  - :ref a ref to be placed on the target element
  - :in-view boolean indicating whether the element is in view

  Notable options:
  - :root element whose bounds are treated as the viewport (default document)
  - :initialInView initial value of the result
  - :rootMargin margin around the root
  - :threshold percentage of element that should be visible before triggering"
  ([]
   (use-in-view nil))
  ([options]
   (let [{target-ref :ref
          target-in-view :inView}
         (bean/bean
          (react-intersection-observer/useInView
           (or (bean/->js options)
               js/undefined)))]
     {:ref target-ref
      :in-view target-in-view})))

(defn within-container-rect?
  "Returns whether an element's bounding rect is in it's container's bounding rect.
  Only checks for vertical dimensions"
  ([element]
   (within-container-rect? element nil))
  ([element container]
   (let [element-rect (.getBoundingClientRect element)
         container-rect (when container (.getBoundingClientRect container))]
     (and (>= (.-top element-rect) (if container-rect (.-top container-rect) 0))
          (<= (.-bottom element-rect) (if container-rect (.-bottom container-rect) (.-innerHeight js/window)))))))

(defn email->mailto
  "Converts a string that looks like an email (for example foo@bar.com) to a mailto: link.
  If the string does not look like an email, returns it unmodified"
  [maybe-email]
  (if (and maybe-email
           (not (re-matches #"^\s*(mailto|http|https|ftp)\s*:.*" maybe-email))
           (email-checker/matches-email maybe-email))
    (str "mailto:" maybe-email)
    maybe-email))

;;

(defn scroll-on-anchor-ref
  "Use this as a react ref when you want the node to be scrolled to immediately on
   page load, if the anchor matches the hash. Example:
   [:div {:id \"my-anchor\" :ref util/scroll-on-anchor-ref}]

   The element will be scrolled to on a url like http://foo.bar/something#my-anchor,
   like the browsers would do if the HTML was ready on load.

   It's important to have this be a stable function otherwise React will call it
   again and again on each render."
  ;;  The alternative was to do this in an effect, but React will run those multiple times
  ;;  in development, making things very annoying. Other global solutions involved doing
  ;;  this at the router level, but you have no way to know when the element will be in the DOM.
  ;;  A library I found was using a MutationObserver on the document, which felt too heavy handed
  [node]
  (when node
    (let [location-hash (.. js/window -location -hash)
          anchor (.getAttribute node "id")]
      (when (= location-hash (str "#" anchor))
        (.scrollIntoView node)))))

(defn scroll-on-mount-ref
  "Use this as a react ref when you want the node to be scrolled to immediately on
   page load

   It's important to have this be a stable function otherwise React will call it
   again and again on each render."
  [node]
  (when node
    (.scrollIntoView
     node
     #js {:block "start"})))

(def deadline-close-millis (* 48 60 60 1000))
(defn deadline-close?
  "Is the deadline getting close? Hardcoded to 48 hours."
  [deadline now]
  (when deadline
    (let [diff-millis (- deadline now)]
      (< 0 diff-millis deadline-close-millis))))

(defn deadline-over?
  "Has the deadline expired?"
  [deadline now]
  (when deadline
    (let [diff-millis (- deadline now)]
      (< diff-millis 0))))

(defn delay-atleast
  "Make sure Promise `p` takes at least `ms` of time."
  [p ms]
  (let [d (js/Promise. (fn [resolve]
                         (js/setTimeout #(resolve nil) ms)))]
    (->
     (js/Promise.all [p d])
     (.then (fn [r]
              (aget r 0))))))

(defn absolute-url
  "Returns an absolute URL string for path, if needed"
  ([path]
   (js/URL. path js/location)))

(defn escape-regexp [s]
  ;; // $& means the whole matched string
  (.replace (str s) (js/RegExp. "[.*+?^${}()|[\\]\\\\]" "g") "\\$&"))

;; calendar-related utils
(defn previous-calendar-year-range [now years]
  (let [previous-year-now (date-subtract now #js {:years years})
        start-of-year (date-set-day-of-year previous-year-now 1)
        end-of-year (-> start-of-year
                        (date-add #js {:years years})
                        (date-subtract #js {:days 1})
                        (date-last-day-of-year))]
    [start-of-year end-of-year]))

(defn previous-calendar-month-range [now months]
  (let [previous-month-now (date-subtract now #js {:months months})
        start-of-month (date-set-date previous-month-now 1)
        end-of-month (-> start-of-month
                         (date-add #js {:months months})
                         (date-subtract #js {:days 1})
                         (date-last-day-of-month))]
    [start-of-month end-of-month]))

(defn expand-date-range-start-end-days [[start end]]
  [(date-start-of-day start)
   (date-end-of-day end)])

(defn calendar-expression->dates
  "Given a map of a calendar expression, return a vector of
   start/end that realizes the calendar expression, expanded to the
   start of day and end of day.

   Supported expressions are {:calendar-years X}, {:calendar-months X}, {:days X}"
  [x]
  (let [now (js/Date.)
        value
        (cond
          (:calendar-years x)
          (previous-calendar-year-range now (:calendar-years x))

          (:calendar-months x)
          (previous-calendar-month-range now (:calendar-months x))

          (:days x)
          [(date-subtract now #js {:days (:days x)}) now]

          :else (throw (ex-info (str "Unsupported calendar expression: " (pr-str x)) x)))]
    (expand-date-range-start-end-days value)))

(defn idea-filter->user-filter [ideabox-id filter]
  (let [{:ideas.filter/keys [dimension operator value]} filter]
    (case dimension
      :activity-published
      {:filter/type :filter/activity
       :filter.activity/subtype (if value
                                  :have-published-ideas
                                  :have-not-published-ideas)}

      :activity-active
      {:filter/type :filter/activity
       :filter.activity/subtype (if value
                                  :have-been-active
                                  :have-not-been-active)}

      :activity-boolean-logged-in
      {:filter/type :filter/activity
       :filter.activity/subtype (if value
                                  :have-sometime-logged-in
                                  :have-never-logged-in)}

      :activity-logged-in-since
      {:filter/type :filter/activity
       :filter.activity/subtype :have-logged-in
       :filter.activity/timestamp value}

      :activity-not-logged-in-since
      {:filter/type :filter/activity
       :filter.activity/subtype :have-not-logged-in
       :filter.activity/timestamp value}

      :scim-inactive
      {:filter/type :filter/scim-inactive
       :filter.scim-inactive/value value}

      :userlabel
      {:filter/type :filter/user-labels
       :filter.user-labels/subtype (case operator :any :include :not-any :exclude)
       :filter.user-labels/selected-labels (into #{} (map (fn [id] {:id id :title "dummy"}) value))}

      :role
      (conj (cond-> {:filter/type :filter/ideabox-role
                     :filter.ideabox-role/subtype (case operator
                                                    :any :include
                                                    :not-any :exclude)
                     :filter.ideabox-role/value value}
              ideabox-id (assoc :filter.ideabox-role/only_ids [ideabox-id]))))))

(defn hex->rgb-commas [hex-color]
  (when hex-color
    (assert (= (first hex-color) "#"))
    (assert (= (count hex-color) 7))
    (let [r (js/parseInt (subs hex-color 1 3) 16)
          g (js/parseInt (subs hex-color 3 5) 16)
          b (js/parseInt (subs hex-color 5 7) 16)]
      (str r ", " g ", " b))))

(defn reverse-cmp [a b]
  (compare b a))

(defn view-transition! [f]
  (if (.-startViewTransition js/document)
    (.startViewTransition js/document f)
    (f)))

(defn set-toggle [s x]
  (if (contains? (set s) x)
    (disj (set s) x)
    (conj (set s) x)))

(defn set-conj [s x]
  (conj (set s) x))

(def nbsp "\u00A0")

(defn focus-callback [element]
  (when element
    (js/setTimeout #(.focus element) 0)))

;; TODO The Suspending type could do with get-value and get-promise methods
(defn use-unwrapped-suspending-value
  "Returns the value of the most recently seen suspending value which
  settled, without triggering the component to suspend."
  [x]
  (let [[result ->result] (hooks/useState (when x (.-value x)))
        idx (React/useRef (volatile! 0))]
    (hooks/useEffect
     (fn []
       (when x
         (let [curr-idx (vswap! (.-current idx) inc)]
           (.then (.-promise x)
                  (fn [result']
                    (when (= curr-idx @(.-current idx))
                      (->result result')))))))
     [x])
    result))

(defn user-persistent-state-boolean [persistent-state-key]
  (let [persistent-state (.getItem js/localStorage persistent-state-key)
        persistent-state (if (= persistent-state "false")
                           false
                           true)
        [state set-state] (hooks/useState persistent-state)
        set-state-persistent (hooks/useCallback (fn [x]
                                                  (let [x (if (fn? x)
                                                            (boolean (x state))
                                                            (boolean x))]
                                                    (.setItem js/localStorage persistent-state-key x)
                                                    (set-state x)))
                                                [state set-state persistent-state-key])]
    (hooks/useEffect
     (fn []
       (when persistent-state-key
         (let [persistent-state (.getItem js/localStorage persistent-state-key)
               persistent-state (if (= persistent-state "false")
                                  false
                                  true)]
           (set-state persistent-state))))
     [persistent-state-key])
    [state set-state-persistent]))

(defn flatten-coll [x]
  (if (coll? x)
    (flatten x)
    x))
